import type { Aabb, CompiledTrack } from './track/types'
import { nearBody } from './track/xindex'

/** Feet-anchored box: `[x, x + w] × [y - h, y]`. */
export interface Body {
  x: number
  y: number
  w: number
  h: number
}

/** Strict overlap: touching edges do not count, so a player resting on a solid never overlaps it. */
export function bodyOverlaps(b: Body, a: Aabb): boolean {
  return b.x < a.x + a.w && b.x + b.w > a.x && b.y - b.h < a.y + a.h && b.y > a.y
}

/** Two feet-anchored boxes overlap (strict). */
export function bodiesOverlap(a: Body, b: Body): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y - a.h < b.y && a.y > b.y - b.h
}

export function centerX(b: Body): number {
  return b.x + b.w / 2
}

export function centerY(b: Body): number {
  return b.y - b.h / 2
}

/** Bottom edge of a box of height `h` whose vertical centre is `cy`. */
export function bottomForCenterY(cy: number, h: number): number {
  return cy + h / 2
}

/** Average of two numbers (vertical integration uses the mean of old and new `vy`). */
export function mean(a: number, b: number): number {
  return (a + b) / 2
}

/** `v` limited to `[-limit, limit]`. */
export function clampAbs(v: number, limit: number): number {
  return Math.max(-limit, Math.min(limit, v))
}

/** `pct` percent of `v`. */
export function pctOf(v: number, pct: number): number {
  return (v * pct) / 100
}

/** Horizontal spans overlap (strict). */
export function spansOverlap(x: number, w: number, ax: number, aw: number): boolean {
  return x < ax + aw && x + w > ax
}

/** True when any solid overlaps the box `[x, x + w] × [y - h, y]`. */
export function overlapsAnySolid(track: CompiledTrack, b: Body): boolean {
  for (const k of nearBody(track.index.solids, b)) {
    if (bodyOverlaps(b, track.solids[k]!)) return true
  }
  return false
}
